import { defineComponent, ref, onMounted, computed, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot, orderBy, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '@/plugins/firebase';
export default defineComponent({
    name: 'RecordingsView',
    setup() {
        const store = useStore();
        const router = useRouter();
        const loading = ref(true);
        const user = computed(() => store.getters.user);
        const recordings = computed(() => store.getters.recordings);
        const isPlaying = ref({});
        const audioElements = {};
        const showNewRecordingModal = ref(false);
        const isUploading = ref(false);
        const uploadError = ref('');
        const newRecording = ref({
            name: '',
            author: '',
            audioFile: null,
            imageFile: null,
            duration: 0,
        });
        const isFormValid = computed(() => {
            return newRecording.value.name.trim() !== '' && newRecording.value.author.trim() !== '' && newRecording.value.audioFile !== null && newRecording.value.imageFile !== null;
        });
        onMounted(() => {
            if (user.value) {
                fetchRecordings();
            }
        });
        const fetchRecordings = () => {
            if (!user.value)
                return;
            const recordingsRef = collection(db, 'Recordings');
            // Create a document reference to the user document
            const userDocRef = doc(db, 'Users', user.value.uid);
            const q = query(recordingsRef, where('user', '==', userDocRef), orderBy('createdAt', 'desc'));
            onSnapshot(q, (snapshot) => {
                const recordingsList = [];
                snapshot.forEach((doc) => {
                    recordingsList.push({
                        id: doc.id,
                        ...doc.data(),
                    });
                });
                store.dispatch('setRecordings', recordingsList);
                loading.value = false;
            });
        };
        const logout = async () => {
            try {
                await signOut(auth);
                router.push('/auth');
            }
            catch (error) {
                console.error('Error signing out:', error);
            }
        };
        const formatDuration = (duration) => {
            const minutes = Math.floor(duration / 60);
            const seconds = duration % 60;
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        };
        const formatDate = (date) => {
            // Handle Firestore Timestamp
            const dateObj = date && typeof date.toDate === 'function' ? date.toDate() : new Date(date);
            return new Intl.DateTimeFormat('da-DK', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).format(dateObj);
        };
        const getStatusClass = (status) => {
            switch (status) {
                case 'ready':
                    return 'bg-green-100 text-green-800';
                case 'processing':
                    return 'bg-yellow-100 text-yellow-800';
                case 'failed':
                    return 'bg-red-100 text-red-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        };
        const getStatusText = (status) => {
            switch (status) {
                case 'ready':
                    return 'Klar';
                case 'processing':
                    return 'Behandler';
                case 'failed':
                    return 'Fejl';
                default:
                    return 'Ukendt';
            }
        };
        const togglePlayback = (recording) => {
            if (!audioElements[recording.id]) {
                audioElements[recording.id] = new Audio(recording.audioUrl);
                audioElements[recording.id].addEventListener('ended', () => {
                    isPlaying.value[recording.id] = false;
                });
            }
            if (isPlaying.value[recording.id]) {
                audioElements[recording.id].pause();
                audioElements[recording.id].currentTime = 0;
                isPlaying.value[recording.id] = false;
            }
            else {
                // Stop any other playing audio
                Object.keys(audioElements).forEach((id) => {
                    if (id !== recording.id && isPlaying.value[id]) {
                        audioElements[id].pause();
                        audioElements[id].currentTime = 0;
                        isPlaying.value[id] = false;
                    }
                });
                audioElements[recording.id].play();
                isPlaying.value[recording.id] = true;
            }
        };
        const handleAudioFileSelect = (event) => {
            const input = event.target;
            if (!input.files || !input.files[0])
                return;
            const file = input.files[0];
            // Check file type
            const validAudioTypes = ['audio/mp3', 'audio/mpeg'];
            if (!validAudioTypes.includes(file.type)) {
                uploadError.value = 'Kun MP3-filer er tilladt';
                return;
            }
            // Check file size (100MB)
            if (file.size > 100 * 1024 * 1024) {
                uploadError.value = 'Lydfilen må ikke være større end 100MB';
                return;
            }
            // Get audio duration using a blob URL (avoids loading entire file into memory as base64)
            const audio = new Audio();
            const blobUrl = URL.createObjectURL(file);
            audio.src = blobUrl;
            audio.onloadedmetadata = () => {
                URL.revokeObjectURL(blobUrl);
                newRecording.value.duration = Math.round(audio.duration);
                uploadError.value = '';
                newRecording.value.audioFile = file;
            };
            audio.onerror = () => {
                URL.revokeObjectURL(blobUrl);
                uploadError.value = 'Kunne ikke læse lydfilen';
                newRecording.value.audioFile = null;
                newRecording.value.duration = 0;
            };
        };
        const handleImageFileSelect = (event) => {
            const input = event.target;
            if (!input.files || !input.files[0])
                return;
            const file = input.files[0];
            // Check file type
            const validImageTypes = ['image/jpeg', 'image/png'];
            if (!validImageTypes.includes(file.type)) {
                uploadError.value = 'Kun JPG og PNG billeder er tilladt';
                return;
            }
            // Check file size (2MB)
            if (file.size > 2 * 1024 * 1024) {
                uploadError.value = 'Billedet må ikke være større end 2MB';
                return;
            }
            // Check image dimensions
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(img.src);
                if (img.width > 1024 || img.height > 1024) {
                    uploadError.value = 'Billedet må ikke være større end 1024x1024 pixels';
                    newRecording.value.imageFile = null;
                    return;
                }
                uploadError.value = '';
                newRecording.value.imageFile = file;
            };
            img.onerror = () => {
                URL.revokeObjectURL(img.src);
                uploadError.value = 'Der opstod en fejl ved validering af billedet';
                newRecording.value.imageFile = null;
            };
            img.src = URL.createObjectURL(file);
        };
        const uploadFile = async (path, file) => {
            const fileRef = storageRef(storage, `${path}`);
            await uploadBytes(fileRef, file);
            return getDownloadURL(fileRef);
        };
        const uploadNewRecording = async () => {
            if (!isFormValid.value || isUploading.value)
                return;
            isUploading.value = true;
            uploadError.value = '';
            try {
                const user = auth.currentUser;
                if (!user) {
                    throw new Error('No user logged in');
                }
                const timestamp = Date.now();
                // Upload files and get URLs
                let audioUrl = '';
                if (newRecording.value.audioFile) {
                    const audioPath = `${user.uid}/${timestamp}_${newRecording.value.audioFile.name}`;
                    audioUrl = await uploadFile(audioPath, newRecording.value.audioFile);
                }
                let imageUrl = '';
                if (newRecording.value.imageFile) {
                    const imagePath = `${user.uid}/${timestamp}_${newRecording.value.imageFile.name}`;
                    imageUrl = await uploadFile(imagePath, newRecording.value.imageFile);
                }
                // Create Firestore document
                const recordingData = {
                    name: newRecording.value.name,
                    author: newRecording.value.author,
                    audioUrl,
                    imageUrl,
                    duration: newRecording.value.duration,
                    createdAt: serverTimestamp(),
                    user: doc(db, 'Users', user.uid),
                };
                await addDoc(collection(db, 'Recordings'), recordingData);
                // Reset form and close modal
                newRecording.value = {
                    name: '',
                    author: '',
                    audioFile: null,
                    imageFile: null,
                    duration: 0,
                };
                showNewRecordingModal.value = false;
            }
            catch (error) {
                console.error('Error uploading recording:', error);
                uploadError.value = 'Der opstod en fejl under upload. Prøv igen.';
            }
            finally {
                isUploading.value = false;
            }
        };
        const closeNewRecordingModal = () => {
            showNewRecordingModal.value = false;
            newRecording.value = {
                name: '',
                author: '',
                audioFile: null,
                imageFile: null,
                duration: 0,
            };
            uploadError.value = '';
        };
        // Clean up audio elements when component is unmounted
        onUnmounted(() => {
            Object.values(audioElements).forEach((audio) => {
                audio.pause();
                audio.src = '';
            });
        });
        return {
            loading,
            recordings,
            logout,
            formatDuration,
            formatDate,
            getStatusClass,
            getStatusText,
            togglePlayback,
            isPlaying,
            showNewRecordingModal,
            newRecording,
            isUploading,
            uploadError,
            isFormValid,
            handleAudioFileSelect,
            handleImageFileSelect,
            uploadNewRecording,
            closeNewRecordingModal,
        };
    },
});
